/**
 * Brand Registry - Centralized brand configuration
 * 
 * This file maps brand IDs to their configurations.
 * Adding new clients only requires adding an entry here.
 */

import { citizensInfoBrandConfig } from './citizensInfo/brand-config';
import { udarasBrandConfig } from './udaras/brand-config';
import { BRAND_ID } from './brand-utils';
import { getCurrentLanguage, type UiLanguage } from '../i18n/language';

// Brand registry - maps brand ID to configuration
export const brandRegistry = {
  'citizensInfo': citizensInfoBrandConfig,
  'udaras': udarasBrandConfig,
  // Future clients can be added here:
  // 'client3': client3BrandConfig,
} as const;

export type BrandId = keyof typeof brandRegistry;
export type BrandConfig = (typeof brandRegistry)[BrandId];

function parseOptionalBooleanBuildFlag(name: string, value: string | undefined): boolean | undefined {
  if (value === undefined || value.trim() === '') {
    return undefined;
  }

  const normalizedValue = value.trim().toLowerCase();
  if (normalizedValue === 'true') {
    return true;
  }
  if (normalizedValue === 'false') {
    return false;
  }

  throw new Error(`${name} must be either "true" or "false".`);
}

const voiceFeatureBuildOverrides = {
  enableVoiceInput: parseOptionalBooleanBuildFlag(
    'VITE_ENABLE_VOICE_INPUT',
    import.meta.env.VITE_ENABLE_VOICE_INPUT,
  ),
  enableVoiceSynthesis: parseOptionalBooleanBuildFlag(
    'VITE_ENABLE_VOICE_SYNTHESIS',
    import.meta.env.VITE_ENABLE_VOICE_SYNTHESIS,
  ),
};

function applyBuildFeatureOverrides<T extends BrandConfig>(config: T): T {
  const { enableVoiceInput, enableVoiceSynthesis } = voiceFeatureBuildOverrides;
  if (enableVoiceInput === undefined && enableVoiceSynthesis === undefined) {
    return config;
  }

  return {
    ...config,
    features: {
      ...config.features,
      ...(enableVoiceInput === undefined ? {} : { enableVoiceInput }),
      ...(enableVoiceSynthesis === undefined ? {} : { enableVoiceSynthesis }),
    },
  } as T;
}

export function getDefaultBrandLanguage(brandId: string | undefined = BRAND_ID): UiLanguage {
  const config = brandRegistry[brandId as BrandId] ?? brandRegistry.citizensInfo;
  return config.defaultLanguage;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function deepMerge<T>(base: T, override: unknown): T {
  if (!isObject(base) || !isObject(override)) {
    return (override as T) ?? base;
  }

  const merged: Record<string, unknown> = { ...base };
  for (const [key, overrideValue] of Object.entries(override)) {
    const baseValue = merged[key];
    if (isObject(baseValue) && isObject(overrideValue)) {
      merged[key] = deepMerge(baseValue, overrideValue);
    } else {
      merged[key] = overrideValue;
    }
  }
  return merged as T;
}

function applyLocaleOverrides<T extends Record<string, unknown>>(config: T, language: UiLanguage): T {
  const locales = (config as any).locales as Partial<Record<UiLanguage, Record<string, unknown>>> | undefined;
  const localeOverride = locales?.[language];
  if (!localeOverride) {
    return config;
  }

  const localized = deepMerge(config, localeOverride);
  return localized;
}

/**
 * Get brand configuration by ID
 * @param brandId - The brand identifier (optional, defaults to current BRAND_ID)
 * @returns Brand configuration object
 */
export function getBrandConfig(brandId: string | undefined = BRAND_ID) {
  // Handle undefined or empty brandId - use current BRAND_ID as fallback
  const safeBrandId = brandId || BRAND_ID;
  
  // Log brand loading for debugging (helps catch brand mismatches)
  // console.log(`🎨 Loading brand config: ${safeBrandId}`);
  
  const config = brandRegistry[safeBrandId as BrandId];
  if (!config) {
    console.warn(`⚠️ Brand '${safeBrandId}' not found, falling back to '${BRAND_ID}'`);
    const fallbackConfig: BrandConfig = brandRegistry[BRAND_ID as BrandId] || brandRegistry.citizensInfo;
    return applyBuildFeatureOverrides(
      applyLocaleOverrides(fallbackConfig, getCurrentLanguage(fallbackConfig.defaultLanguage)),
    );
  }

  const language = getCurrentLanguage(config.defaultLanguage);
  const localizedConfig = applyLocaleOverrides(config, language);

  // console.log(`✅ Brand config loaded:`, { appName: config.appName, footer: config.footer.centerText });
  return applyBuildFeatureOverrides(localizedConfig);
}

/**
 * Get all available brand IDs
 * @returns Array of available brand IDs
 */
export function getAvailableBrands(): BrandId[] {
  return Object.keys(brandRegistry) as BrandId[];
}

/**
 * Check if a brand ID is valid
 * @param brandId - The brand identifier to check
 * @returns True if brand exists
 */
export function isValidBrand(brandId: string): brandId is BrandId {
  return brandId in brandRegistry;
}
